/**
 * The retro intro: the switch into the retro theme, staged as the opening of
 * "Back in Black". The track plays from T = 0 and the picture follows its
 * clock, so every moment here is keyed to `CUE` and the beat as measured from
 * the recording.
 *
 *   Count-in  the standard board flickers like a dying fluorescent on each
 *             hi-hat while the camera slowly pushes in
 *   Riff 1    the first chord kills the power (CRT collapse and explosion);
 *             the next two chords are lightning flashes of retro columns; the
 *             lick types a reboot terminal on black
 *   Riff 2    each chord slams a retro column in behind a black wipe; the lick
 *             snaps the rest of the board in
 *   Drums     the board powers on with a bloom, scanlines slam in and a
 *             barrage of explosions, smoke and embers erupts
 *
 * It runs inside a view transition, so the standard look is a real snapshot.
 * The two board snapshots are driven through the custom properties that the
 * `::view-transition-*` rules in `retro.css` read; everything else is drawn on
 * the `rm-fx` canvas, which has its own transition group above the page.
 */

const B = 0.66; // one beat, as the track actually plays it (about 91 BPM)
/** Section starts in seconds of the trimmed track, measured from its chord onsets. */
const CUE = { countIn: 0, riff1: 5.415, riff2: 10.718, drums: 15.969, end: 18.969 };
/** The eight hi-hat ticks of the count-in, as played. */
const TICKS = [0.06, 0.739, 1.419, 2.098, 2.777, 3.456, 4.109, 4.762];
/** Where the riff's three chords fall, in beats: E on the downbeat, D a beat later, A two after that. */
const CHORD_BEATS = [0, 1, 3];
const TERMINAL = [
  '> SIGNAL LOST',
  '> POWER: CRITICAL',
  '> REBOOTING KERNEL',
  '> LOADING THEME  [RETRO]',
  '> ARMING CHARGES',
  '> BACK IN BLACK',
];
const DEBRIS = ['#ffd27a', '#ff8a2a', '#f2a334', '#5a2a12', '#2a2f3a'];
/** The track, trimmed so the count-in starts at 0. `AUDIO_OFFSET` is where in the file T = 0 lands. */
const AUDIO_URL = '/audio/retro-intro.mp3';
const AUDIO_OFFSET = 0;

// ------------------------------------------------------------- maths --

/** Deterministic noise, so the chaos stutters the same way every time. */
const hash = (n: number): number => {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
/** An impulse at `t`, dying away at rate `k`. */
const decay = (T: number, t: number, k: number): number => (T >= t ? Math.exp(-k * (T - t)) : 0);
const easeInQuad = (t: number): number => t * t;
const easeOutQuad = (t: number): number => t * (2 - t);
const easeOutCubic = (t: number): number => 1 + (t - 1) ** 3;
const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t ** 3 : 1 + 4 * (t - 1) ** 3);
const easeOutExpo = (t: number): number => (t === 1 ? 1 : 1 - 2 ** (-10 * t));
/** `from` to `to` across [start, end], holding either side. */
const tween =
  (from: number, to: number, start: number, end: number, ease: (t: number) => number) =>
  (T: number): number =>
    from + (to - from) * ease(clamp01((T - start) / (end - start)));

// ------------------------------------------------------------- scene --

/** A full-height strip (a column) or a full-width band (header, filter bar). */
type Slice = { x: [number, number] } | { y: [number, number] };
type Reveal = { t: number; slice: Slice };
type Hit = { t: number; x: number; y: number; big?: boolean; spark?: boolean };

/**
 * Where the slams land: each board column as a strip, then the shell header
 * and the filter bar as bands. Measured once the DOM is retro, since that is
 * the layout the new snapshot shows. Off the board view, or with nothing to
 * measure, five equal strips stand in.
 */
function measure(W: number, H: number): { columns: Slice[]; head: Slice; filt: Slice } {
  const rects = [...document.querySelectorAll("section[class*='column']")]
    .map((el) => el.getBoundingClientRect())
    .filter((r) => Math.min(W, r.right) - Math.max(0, r.left) >= 40);
  const columns: Slice[] = rects.length
    ? rects.map((r): Slice => ({ x: [Math.max(0, r.left), Math.min(W, r.right)] }))
    : Array.from({ length: 5 }, (_, i): Slice => ({ x: [(i * W) / 5, ((i + 1) * W) / 5] }));
  // The shell header is the first <header> in the document; column headers follow it.
  const headBottom = document.querySelector('header')?.getBoundingClientRect().bottom ?? H * 0.06;
  const boardTop = rects.length ? Math.min(...rects.map((r) => r.top)) : H * 0.12;
  return { columns, head: { y: [0, headBottom] }, filt: { y: [headBottom, Math.max(headBottom, boardTop)] } };
}

const maskOf = (slices: Slice[]): string =>
  slices.length
    ? slices
        .map((s) =>
          'x' in s
            ? `linear-gradient(90deg, transparent ${s.x[0]}px, #000 ${s.x[0]}px, #000 ${s.x[1]}px, transparent ${s.x[1]}px)`
            : `linear-gradient(180deg, transparent ${s.y[0]}px, #000 ${s.y[0]}px, #000 ${s.y[1]}px, transparent ${s.y[1]}px)`,
        )
        .join(', ')
    : 'none';

const centerOf = (s: Slice, W: number, H: number): { x: number; y: number } =>
  'x' in s ? { x: (s.x[0] + s.x[1]) / 2, y: H * 0.5 } : { x: W / 2, y: (s.y[0] + s.y[1]) / 2 };

/** The whole cue sheet, laid over the measured board. */
function choreograph(W: number, H: number) {
  const { columns, head, filt } = measure(W, H);
  const { riff1: R1, riff2: R2, drums: DR } = CUE;
  const chord1 = R1;
  const chord2 = R1 + B;
  const chord3 = R1 + 3 * B;
  const L1 = R1 + 5 * B; // the lick that ends each riff
  const L2 = R2 + 5 * B;

  // Riff 2: the chords take the first three columns, the lick snaps in the rest,
  // then the header and the filter bar, and the whole board is in two beats later.
  const rest = columns.slice(3);
  const reveals: Reveal[] = [
    ...columns.slice(0, 3).map((slice, i) => ({ t: R2 + (CHORD_BEATS[i] ?? 0) * B, slice })),
    ...rest.map((slice, i) => ({ t: L2 + ((i * B) / 2) / Math.max(1, rest.length - 1), slice })),
    { t: L2 + B, slice: head },
    { t: L2 + 1.5 * B, slice: filt },
  ];
  const tFull = L2 + 2 * B;

  // Riff 1: lightning flashes of the retro board, one column and then three.
  const flashes = [
    { t: chord2, slices: columns.slice(0, 1) },
    { t: chord3, slices: columns.slice(0, 3) },
  ];

  const hits: Hit[] = [{ t: chord1, x: W / 2, y: H * 0.52, big: true }];
  flashes.forEach((f, i) => f.slices.slice(i).forEach((s) => hits.push({ t: f.t, ...centerOf(s, W, H) })));
  for (let j = 0; j < 6; j++) {
    hits.push({ t: L1 + (j * B) / 2, x: W * (0.1 + hash(j + 11) * 0.8), y: H * (0.19 + hash(j + 31) * 0.65), spark: true });
  }
  for (const r of reveals) hits.push({ t: r.t, ...centerOf(r.slice, W, H), big: 'x' in r.slice && r.t < L2 });
  hits.push({ t: L2 + 2 * B, x: W * 0.26, y: H * 0.28 }, { t: L2 + 2.5 * B, x: W * 0.73, y: H * 0.74 });
  for (let j = 0; j < 9; j++) {
    hits.push({ t: DR + j * 0.07, x: W * (0.06 + hash(j + 51) * 0.875), y: H * (0.11 + hash(j + 71) * 0.78), big: j % 3 === 0 });
  }

  const punches = [...reveals.map((r) => r.t), chord1, chord2, chord3, DR];
  return { reveals, tFull, flashes, hits, punches, chord1, L1 };
}

// --------------------------------------------------------------- fx --

type Ctx = CanvasRenderingContext2D;

/** A black band leaning across the screen ahead of a slam. */
function band(ctx: Ctx, x: number, W: number, H: number, k: number): void {
  const w = 420 * k;
  const lean = Math.tan((-12 * Math.PI) / 180);
  const top = -100 * k;
  const bottom = H + 100 * k;
  const cy = H / 2; // skewed about its middle, so it leans without walking
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.9)';
  ctx.shadowBlur = 40 * k;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.moveTo(x + lean * (top - cy), top);
  ctx.lineTo(x + w + lean * (top - cy), top);
  ctx.lineTo(x + w + lean * (bottom - cy), bottom);
  ctx.lineTo(x + lean * (bottom - cy), bottom);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** The reboot terminal: a line every half beat, typed in, with a blinking cursor. */
function terminal(ctx: Ctx, T: number, f: number, L1: number, W: number, H: number, k: number): void {
  const size = Math.max(11, Math.round(24 * k));
  ctx.save();
  ctx.globalAlpha = T < CUE.riff2 ? 1 : 1 - clamp01((T - CUE.riff2) / 0.12);
  ctx.font = `${size}px "Press Start 2P", monospace`;
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#f2a334';
  ctx.shadowColor = 'rgba(242,163,52,0.7)';
  ctx.shadowBlur = 12 * k;
  TERMINAL.forEach((line, i) => {
    const t0 = L1 + (i * B) / 2;
    if (T < t0) return;
    const n = Math.ceil(clamp01((T - t0) / 0.16) * line.length);
    const cursor = (i === TERMINAL.length - 1 || T < t0 + B / 2) && (f >> 4) % 2 === 0 ? '█' : '';
    ctx.fillText(line.slice(0, n) + cursor, W * 0.27, H * 0.33 + i * size * 1.6);
  });
  ctx.restore();
}

/** Fireball, shockwave ring, white core and tumbling debris; `p` runs 0 to 1. */
function explosion(ctx: Ctx, h: Hit, p: number, seed: number, k: number): void {
  if (p <= 0 || p >= 1) return;
  const s = (h.big ? 1.8 : h.spark ? 0.45 : 1) * k;
  const q = easeOutCubic(p);
  const { x, y } = h;
  const r = 150 * s * (0.15 + q * 1.1);
  const fire = ctx.createRadialGradient(x, y, 0, x, y, r);
  fire.addColorStop(0, '#fff8dc');
  fire.addColorStop(0.22, '#ffb347');
  fire.addColorStop(0.4, '#ff6a1a');
  fire.addColorStop(0.6, 'rgba(160,40,10,0.6)');
  fire.addColorStop(0.72, 'rgba(160,40,10,0)');
  ctx.globalAlpha = 1 - easeInQuad(p);
  ctx.fillStyle = fire;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = (1 - p) * 0.9;
  ctx.strokeStyle = '#ffd27a';
  ctx.lineWidth = 4 * s;
  ctx.beginPath();
  ctx.arc(x, y, 100 * s * q * 2.8, 0, Math.PI * 2);
  ctx.stroke();
  if (p < 0.3) {
    ctx.globalAlpha = 1 - p / 0.3;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(x, y, 60 * s * (1 + q * 2), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = clamp01((1 - p) * 1.6);
  for (let j = 0; j < 14; j++) {
    const a = hash(seed + j * 3) * Math.PI * 2;
    const d = (90 + hash(seed + j * 3 + 1) * 260) * s * q;
    const sz = (5 + hash(seed + j * 3 + 2) * 11) * s;
    ctx.fillStyle = DEBRIS[j % DEBRIS.length] ?? '#f2a334';
    ctx.save();
    ctx.translate(x + Math.cos(a) * d, y + Math.sin(a) * d + 420 * p * p * s);
    ctx.rotate((p * 720 * hash(seed + j) * Math.PI) / 180);
    ctx.fillRect(-sz / 2, -sz / 2, sz, sz);
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/** Seven puffs rising and thinning out of a hit; `p` runs 0 to 1. */
function smoke(ctx: Ctx, h: Hit, p: number, seed: number, k: number): void {
  if (p <= 0.05 || p >= 1) return;
  const s = (h.big ? 1.7 : 1) * k;
  const q = easeOutQuad(clamp01((p - 0.05) / 0.95));
  const warm = clamp01(1 - p * 3);
  const op = Math.min(p / 0.12, 1) * (1 - p) * 0.9;
  for (let j = 0; j < 7; j++) {
    const a = hash(seed + j * 5) * Math.PI * 2;
    const sz = (70 + hash(seed + j * 5 + 1) * 90) * s * (0.5 + q * 1.3);
    const cx = h.x + Math.cos(a) * (40 + hash(seed + j * 5 + 2) * 90) * s * q + Math.sin(p * 6 + j) * 14 * k;
    const cy = h.y + Math.sin(a) * 30 * s * q - (140 + hash(seed + j * 5 + 3) * 200) * s * q;
    const puff = ctx.createRadialGradient(cx, cy, 0, cx, cy, sz / 2);
    puff.addColorStop(0, `rgba(${90 + warm * 120},${80 + warm * 50},75,${op})`);
    puff.addColorStop(0.45, `rgba(48,48,54,${op * 0.9})`);
    puff.addColorStop(0.62, `rgba(30,30,34,${op * 0.35})`);
    puff.addColorStop(0.75, 'rgba(20,20,24,0)');
    ctx.fillStyle = puff;
    ctx.beginPath();
    ctx.arc(cx, cy, sz / 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ------------------------------------------------------------- show --

/**
 * Plays the intro around `apply`, which flips the theme. Without the View
 * Transitions API, or under reduced motion, it just calls `apply`. Escape
 * skips to the end.
 */
let running = false;

export async function runRetroIntro(apply: () => void): Promise<void> {
  if (running) return; // a second press mid-show changes nothing
  const root = document.documentElement;
  const W = innerWidth;
  const H = innerHeight;
  const k = Math.max(0.5, Math.min(W / 1920, H / 1080)); // effect sizes are authored at 1080p
  const dpr = Math.min(2, devicePixelRatio || 1);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!document.startViewTransition || !ctx || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    apply();
    return;
  }
  running = true;

  // The track is the clock: the picture follows `audio.currentTime`, so it stays
  // on the beat whatever the frame rate does. `play` is called inside the user's
  // gesture, then the show waits for the track to roll; if it cannot, the show
  // runs silent on the frame clock instead.
  const audio = new Audio(AUDIO_URL);
  const rolling = audio.play().then(() => true, () => false);
  const onTrack = await Promise.race([rolling, new Promise<boolean>((r) => setTimeout(() => r(false), 4000))]);
  if (!onTrack) void rolling.then((late) => late && audio.pause());

  // Only writes a property when its value changes, so quiet frames cost no style work.
  const vars = new Map<string, string>();
  const setVar = (name: string, value: string) => {
    if (vars.get(name) === value) return;
    vars.set(name, value);
    root.style.setProperty(name, value);
  };

  root.dataset.intro = '';
  // A safety net only: the frame loop ends the show on the beat.
  setVar('--rm-intro-ms', `${(CUE.end + 5) * 1000}ms`);
  canvas.className = 'rm-fx';
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  ctx.scale(dpr, dpr);
  document.body.appendChild(canvas);

  const transition = document.startViewTransition(apply);
  let raf = 0;
  let start = -1;

  transition.ready.then(
    () => {
      // The DOM is retro from here, which is the layout the new snapshot shows.
      const { reveals, tFull, flashes, hits, punches, chord1, L1 } = choreograph(W, H);
      const { riff1: R1, drums: DR } = CUE;

      const frame = (now: number) => {
        if (start < 0) start = now;
        const T = onTrack ? audio.currentTime - AUDIO_OFFSET : (now - start) / 1000;
        if (T >= CUE.end || audio.ended) {
          transition.skipTransition();
          return;
        }
        audio.volume = clamp01((CUE.end - T) / 0.6); // fades out under the last beat
        const f = Math.floor(T * 60);

        // Count-in: a dying fluorescent on every hi-hat, plus the odd buzz.
        let tick = 0;
        for (const t of TICKS) tick = Math.max(tick, decay(T, t, 14));
        const buzz = T < R1 && hash(f) > 0.92 ? 0.35 : 0;

        // First chord: the standard board collapses to a line, then to nothing.
        const pdY = tween(1, 0.004, chord1, chord1 + 0.14, easeOutExpo)(T);
        const pdX = tween(1, 0, chord1 + 0.14, chord1 + 0.28, easeOutExpo)(T);
        setVar('--rm-old-transform', `scale(${pdX.toFixed(4)}, ${pdY.toFixed(4)})`);
        setVar('--rm-old-filter', `brightness(${(T >= chord1 ? 2.5 : 1 - 0.55 * tick - buzz).toFixed(3)})`);
        setVar('--rm-old-opacity', T < chord1 + 0.28 ? '1' : '0');

        // The retro board: lightning flashes, then slammed in slice by slice, dim until the drums.
        const flash = flashes.find((fl) => T >= fl.t && T < fl.t + 0.24);
        const shown = reveals.filter((r) => T >= r.t);
        const converted = T >= tFull;
        const lit = T < DR ? 0.55 : tween(3.2, 1, DR, DR + 0.45, easeOutCubic)(T);
        let slam = 0;
        for (const r of shown) slam = Math.max(slam, 1 - clamp01((T - r.t) / 0.2));
        if (flash) {
          setVar('--rm-new-mask', maskOf(flash.slices));
          setVar('--rm-new-opacity', hash(f) > 0.35 ? '1' : '0.25');
          setVar('--rm-new-filter', 'brightness(1.8) contrast(1.2)');
        } else {
          setVar('--rm-new-mask', converted ? 'none' : maskOf(shown.map((r) => r.slice)));
          setVar('--rm-new-opacity', converted || shown.length ? '1' : '0');
          setVar('--rm-new-filter', `brightness(${(lit + 1.65 * slam).toFixed(3)}) contrast(1.1)`);
        }

        // Camera: shake on every hit, a punch on every accent, a slow push-in and release.
        let amp = tick * 3;
        for (const h of hits) amp += decay(T, h.t, 5) * (h.big ? 34 : h.spark ? 6 : 16);
        amp *= k;
        const dx = amp > 0.3 ? (hash(f * 3 + 1) - 0.5) * 2 * amp : 0;
        const dy = amp > 0.3 ? (hash(f * 3 + 2) - 0.5) * 2 * amp : 0;
        let punch = 0;
        for (const t of punches) punch = Math.max(punch, decay(T, t, 6) * 0.05);
        const strut = T < DR ? tween(1, 1.07, CUE.countIn, R1, easeInOutCubic)(T) : tween(1.07, 1, DR, DR + 2.4, easeInOutCubic)(T);
        const pan = tween(0, -40 * k, DR, DR + 2.8, easeInOutCubic)(T);
        setVar('--rm-cam', `translate(${(dx + pan).toFixed(1)}px, ${dy.toFixed(1)}px) scale(${(strut + punch).toFixed(4)})`);
        setVar('--rm-scanlines', T >= DR ? '1' : '0');

        // Everything else is paint.
        ctx.clearRect(0, 0, W, H);
        for (const r of reveals) {
          if (T >= r.t && T < r.t + 0.3) band(ctx, tween(-520 * k, W + 200 * k, r.t, r.t + 0.3, easeOutExpo)(T), W, H, k);
        }
        if (T >= L1 - 0.1 && T < CUE.riff2 + 0.12) terminal(ctx, T, f, L1, W, H, k);
        hits.forEach((h, i) => {
          smoke(ctx, h, (T - h.t) / (h.big ? 2.6 : h.spark ? 1.2 : 2.2), i * 23 + 5, k);
          explosion(ctx, h, (T - h.t) / (h.big ? 1.1 : 0.9), i * 17 + 3, k);
        });
        const ember = T >= DR ? clamp01((T - DR) / 0.4) : 0;
        for (let j = 0; ember > 0 && j < 22; j++) {
          const speed = (90 + hash(j + 50) * 120) * k;
          const y = H - (((T - DR) * speed + hash(j) * H) % H);
          const x = hash(j + 20) * W + Math.sin(T * 2 + j) * 20 * k;
          const sz = (4 + hash(j + 70) * 5) * k;
          ctx.globalAlpha = ember * (0.4 + 0.6 * hash(j + 90));
          ctx.fillStyle = j % 3 ? '#f2a334' : '#ff6a1a';
          ctx.fillRect(x, y, sz, sz);
        }
        ctx.globalAlpha = 1;
        if (T >= chord1 && T < chord1 + 0.5) {
          // The last line of the dying picture.
          ctx.globalAlpha = 1 - clamp01((T - chord1 - 0.28) / 0.22);
          ctx.shadowColor = 'rgba(255,255,255,0.8)';
          ctx.shadowBlur = 30 * k;
          ctx.fillStyle = '#fff';
          ctx.fillRect(W / 2 - (W / 2) * pdX, H / 2 - 2 * k, W * pdX + 6 * k, 4 * k);
          ctx.shadowBlur = 0;
          ctx.globalAlpha = 1;
        }
        let hitFlash = 0;
        for (const h of hits) if (!h.spark) hitFlash = Math.max(hitFlash, decay(T, h.t, 22) * 0.55);
        if (hitFlash > 0.002) {
          ctx.globalAlpha = hitFlash;
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, W, H);
          ctx.globalAlpha = 1;
        }

        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    },
    () => undefined,
  );

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') transition.skipTransition();
  };
  window.addEventListener('keydown', onKey);
  transition.finished.finally(() => {
    cancelAnimationFrame(raf);
    audio.pause();
    running = false;
    window.removeEventListener('keydown', onKey);
    for (const name of vars.keys()) root.style.removeProperty(name);
    delete root.dataset.intro;
    canvas.remove();
  });
}
